/**
 * /earthworks/jobs — the job register (GET /earthworks/jobs, EW-1).
 *
 * Full width; a row opens the job on its own page. Filters (status, job type,
 * customer, site, search) live in the URL so Back returns to the list as it
 * was, and every filter is server-side. A field hand's list is the API's —
 * only the jobs they are crewed on — and New job is not offered to them.
 *
 * The holdback column is headed from meta.holdbackLabel (Holdback in CA,
 * Retainage in US), never a literal. Dates are calendar days.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarClock, ClipboardList, HardHat, Plus, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useEwJobs, useEwMeta, useEwSites } from '../../hooks/useEarthworks';
import { queryLoadFailure } from '../../lib/load-failure';
import { canDispatchEw, ewDay, ewJobStatusSkin, fmtMoney, fmtPct, holdbackLabel } from '../../lib/earthworks';
import type { EwJob } from '../../types/earthworks';
import { useOpenRecord } from '../downhole/recordPage';
import { EW_EYEBROW, EW_INPUT, EwBadge, EwChip, EwChipRow, EwEmpty, EwPanel, EwRegister, type EwColumn } from './EwBits';

/** A job's holdback: its % when on, `Off` when off, `—` when on with no % recorded. */
export const jobHoldback = (j: Pick<EwJob, 'holdbackEnabled' | 'holdbackPct'>): string =>
  j.holdbackEnabled ? fmtPct(j.holdbackPct) : 'Off';

export default function EwJobsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const jobType = searchParams.get('jobType') ?? '';
  const siteId = searchParams.get('siteId') ?? '';
  const customerId = searchParams.get('customerId') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(qParam);

  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== qParam) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const catalog = useEwMeta().data?.data ?? null;
  const filters = useMemo(() => ({ status, jobType, siteId, customerId, q: qParam }), [status, jobType, siteId, customerId, qParam]);
  const filtered = !!(status || jobType || siteId || customerId || qParam);
  const listQuery = useEwJobs(filters);
  const allQuery = useEwJobs();
  const sitesQuery = useEwSites();
  const jobs = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta ?? allQuery.data?.meta ?? null;
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (j: EwJob) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canDispatchEw(user?.role);
  const hbLabel = holdbackLabel(meta);

  const customers = useMemo(() => {
    const seen = new Map<string, string>();
    (all ?? []).forEach((j) => seen.set(j.customerId, j.customerName ?? '—'));
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);
  const sites = useMemo(
    () => (sitesQuery.data?.data ?? []).filter((s) => !customerId || s.customerId === customerId),
    [sitesQuery.data, customerId],
  );

  const columns: EwColumn<EwJob>[] = [
    {
      key: 'number',
      header: 'Job',
      render: (j) => (
        <>
          <p className="font-mono font-extrabold text-white">{j.jobNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{j.jobTypeLabel || '—'}</p>
        </>
      ),
    },
    { key: 'customer', header: 'Customer', render: (j) => j.customerName || '—' },
    { key: 'site', header: 'Site', render: (j) => j.siteName || '—' },
    { key: 'contract', header: 'Contract', render: (j) => j.contractTypeLabel || '—' },
    { key: 'value', header: 'Value', align: 'right', render: (j) => <span className="font-mono">{fmtMoney(j.contractValue, j.currency)}</span> },
    { key: 'holdback', header: hbLabel, align: 'right', render: (j) => jobHoldback(j) },
    { key: 'start', header: 'Scheduled', render: (j) => `${ewDay(j.scheduledStart)}${j.scheduledEnd ? ` → ${ewDay(j.scheduledEnd)}` : ''}` },
    { key: 'supervisor', header: 'Supervisor', render: (j) => j.supervisorName || '—' },
    { key: 'crew', header: 'Crew', align: 'right', render: (j) => displayValue(j.crewCount ?? undefined) },
    { key: 'units', header: 'Units', align: 'right', render: (j) => displayValue(j.equipmentCount ?? undefined) },
    { key: 'status', header: 'Status', render: (j) => <EwBadge skin={ewJobStatusSkin(j.status)} value={j.status} /> },
  ];

  return (
    <div data-testid="ew-jobs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title="Jobs"
        description="Excavation, grading, trenching and site work — customer, site, crew and units."
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/earthworks/jobs/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New job
            </button>
          ) : undefined
        }
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Jobs', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Scheduled', tone: 'info', loading: countsLoading, value: countOf((j) => j.status === 'scheduled') },
          { icon: <HardHat className="size-4" aria-hidden="true" />, label: 'In progress', tone: 'accent', loading: countsLoading, value: countOf((j) => j.status === 'in_progress') },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <EwChipRow label="Filter by status">
            <EwChip label="All" active={!status} onClick={() => setParam('status', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {(catalog?.jobStatuses ?? []).map((s) => (
              <EwChip
                key={s.value}
                label={s.label}
                active={status === s.value}
                onClick={() => setParam('status', s.value)}
                count={countsLoading ? undefined : countOf((j) => j.status === s.value)}
              />
            ))}
          </EwChipRow>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem_12rem_12rem]">
            <label className="relative block">
              <span className="sr-only">Search job, customer, site, PO or AFE</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search job, customer, site, PO or AFE"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select className={EW_INPUT} aria-label="Filter by job type" value={jobType} onChange={(e) => setParam('jobType', e.target.value)}>
              <option value="">All job types</option>
              {(catalog?.jobTypes ?? []).map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <select className={EW_INPUT} aria-label="Filter by customer" value={customerId} onChange={(e) => setParam('customerId', e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select className={EW_INPUT} aria-label="Filter by site" value={siteId} onChange={(e) => setParam('siteId', e.target.value)}>
              <option value="">All sites</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <EwPanel
            eyebrow="Job register"
            title="Earthworks jobs"
            testId="ew-job-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && jobs.length > 0
                ? filtered
                  ? `Showing ${jobs.length} of ${countOf(() => true)} jobs`
                  : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <EwRegister
              query={listQuery}
              rows={jobs}
              columns={columns}
              rail={(j) => ewJobStatusSkin(j.status).rail}
              onOpen={(j) => openRecord(`/earthworks/jobs/${j.id}`)}
              subject="jobs"
              testId="ew-job"
              minWidth="min-w-[1240px]"
              empty={filtered ? <EwEmpty>No job matches these filters.</EwEmpty> : <EwEmpty>There are no earthworks jobs yet.</EwEmpty>}
              card={(j) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{j.jobNumber || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {j.customerName || '—'} · {j.siteName || '—'}
                      </p>
                    </div>
                    <EwBadge skin={ewJobStatusSkin(j.status)} value={j.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {ewDay(j.scheduledStart)} · {j.jobTypeLabel || '—'} · {hbLabel} {jobHoldback(j)} · Crew {displayValue(j.crewCount ?? undefined)}
                  </p>
                </>
              )}
            />
          </EwPanel>
        </div>
      </section>
    </div>
  );
}
